import { useEffect, useState, useCallback } from 'react';
import type { FinancialAccountNode } from '../services/orgs/interfaces';
import { extractErrorMessage } from '../util/errorMessage';
import { getPrimaryKycData } from '../services/vault/kyc';

export default function useRaisePrimaryKyc({
  orgDataIsFinished,
  financialAccountNode,
  financialAccountIsFinished,
  provider,
  financialAccountRootsAreFinished,
}: {
  orgDataIsFinished: boolean;
  financialAccountNode?: FinancialAccountNode;
  financialAccountIsFinished: boolean;
  provider: VaultProvider;
  financialAccountRootsAreFinished: boolean;
}): {
  primaryKyc?: Kyc;
  errorMessage?: string;
  isFinished: boolean;
  representativeStripePersonId?: string;
} {
  const [primaryKyc, setPrimaryKyc] = useState<Kyc>();
  const [representativeStripePersonId, setRepresentativeStripePersonId] =
    useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isFinished, setIsFinished] = useState(false);

  const gatherData = useCallback(async () => {
    if (!orgDataIsFinished) {
      return;
    }

    if (financialAccountIsFinished && financialAccountNode === undefined) {
      setIsFinished(true);
      return;
    }

    if (
      primaryKyc === undefined &&
      !!financialAccountNode?.finAcctId &&
      financialAccountRootsAreFinished
    ) {
      try {
        const retrievedKycData = await getPrimaryKycData(
          financialAccountNode.finAcctId,
          provider,
        );

        setRepresentativeStripePersonId(retrievedKycData.stripePersonId);

        if (retrievedKycData.kyc !== undefined) {
          setPrimaryKyc(retrievedKycData.kyc);
        }
      } catch (e) {
        setErrorMessage(extractErrorMessage(e));
      } finally {
        setIsFinished(true);
      }
    }
  }, [
    financialAccountIsFinished,
    financialAccountNode,
    financialAccountRootsAreFinished,
    orgDataIsFinished,
    primaryKyc,
    provider,
  ]);
  useEffect(() => {
    gatherData();
  }, [gatherData]);

  return {
    primaryKyc,
    errorMessage,
    isFinished,
    representativeStripePersonId,
  };
}
